import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Uuid} from "../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {Sort} from "@angular/material/sort";
import {delay, distinctUntilChanged, first, takeUntil} from "rxjs/operators";
import {
  RECEIVABLES_IMPORT_ROW_STATUS_CANCELLED,
  RECEIVABLES_IMPORT_ROW_STATUS_PAID
} from "../../../../shared/services/dict.service";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {ReceivablesImportRow} from "../../../../shared/interface/receivables-import-row";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../../shared/services/auth.service";
import {FilterService} from "../../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {ReceivablesImportsRowService} from "../../../../shared/services/receivables-imports-row.service";
import {ReplaySubject} from "rxjs";
import {NgForm} from "@angular/forms";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {InsuranceCompany} from "../../../../shared/interface/insurance-company";
import {ReceivablesPaymentImportRowService} from "../../../../shared/services/receivables-payment-import-row.service";
import {ReceivablesPaymentImportRow} from "../../../../shared/interface/receivables-payment-import-row";

@Component({
  selector: 'app-all-receivables-payment-import-row-list',
  templateUrl: './all-receivables-payment-import-row-list.component.html',
  styleUrls: ['./all-receivables-payment-import-row-list.component.scss']
})
export class AllReceivablesPaymentImportRowListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() statusKey?: string[] = null;
  @Input() partnerId?: string = null;
  @Input() showFilter = true;
  @Input() balanceTitle?: string = null;
  @Input() receivablesBalanceId?: string = null;
  @Input() displayedColumns: string[] = [ 'title', 'partner', 'owner', 'postingData', 'statement', 'amount', 'status'];
  @Input() embeddedView = false;
  @Input() ownerName?: string = null;

  initialFilters: any;
  rows: ReceivablesPaymentImportRow[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'createdAt';
  sortOrder = 'desc';
  selectedInsuranceCompanies: InsuranceCompany[] = [];
  serviceKey = '';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private receivablesPaymentImportRowService: ReceivablesPaymentImportRowService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.receivablesPaymentImportRowService.rowListSubject
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      ).subscribe({
      next: (response: CountedAndPaginated<ReceivablesPaymentImportRow>) => {
        this.rows = response.items;
        this.totalData = response.pagination.total;
      },
      error: (response: HttpErrorResponse) => {
        this.snack.open('Przy ładowaniu danych wystąpił błąd', response.status.toString(), {duration: 2000});
      }
    });
    this.parseFilters();
  }

  ngOnInit(): void {
  }
  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$transferTitle: 'string',
      contains$ownerName: 'string',
      gte$postingDate: 'string',
      lte$postingDate: 'string',
      statementTransferTitle: 'string',
      transferAmount: 'string',
      in$status: 'int[]',
      policyNumber: 'string',
      balanceTitle: 'string',
      manualPayment: 'int'
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.ownerName = this.ownerName ?? data.ownerName ??  null;
        if (!this.embeddedView) {
          this.store
            .pipe(
              select(getCurrentRouteState),
              delay(0),
              distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
              takeUntil(this.destroyed$),
            )
            .subscribe((state: RouterStateUrl) => {
              this.parseFilters(state.params);
              this.paginator.pageIndex = state.params.page;
              this.paginator.pageSize = state.params.pageSize;
              this.loadListData();
            });
        } else {
          this.loadListData();
        }
      });
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      if (this.embeddedView) {
        this.loadListData();
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
      }
    });

  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }
    if (null !== this.partnerId) {
      filters.partnerId = this.partnerId;
    }
    if (null !== this.balanceTitle) {
      filters.balanceTitle = this.balanceTitle;
    }
    if (null !== this.receivablesBalanceId) {
      filters.receivablesBalance = this.receivablesBalanceId;
    }

    return filters;
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      sortBy: $event.active,
      sortOrder: $event.direction,
      page: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value
    })]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  async loadListData(): Promise<void> {
    this.receivablesPaymentImportRowService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      this.serviceKey,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value,
      })]);
    }
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }]);
    }
  }

  downloadReport(): void {
    this.receivablesPaymentImportRowService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }
}
